import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import useGetLoggedInUser from 'services/hooks/LoggedInUser/useGetLoggedInUser';
import {
  isPublicBrowsePath,
  isMembershipFinalizingPath,
} from 'utils/dashboardPageAccess';
import { getStoredToken } from 'utils/authToken';
import { navigateToDashboard } from 'utils/navigation';
import PageLoader from './LoadingButton/PageLoader';
import axios from 'axios';
import apiClient, { v1Backend } from 'services/api/client';
import { clearAuthToken } from 'utils/authToken';

/** True if we have valid user data (axios response body). */
function hasValidUserData(queryDataOrAxiosRes) {
  const body = queryDataOrAxiosRes?.data;
  return body && body !== false && body !== null && body !== '';
}

function isMembershipExpired(queryDataOrAxiosRes) {
  const memberships = queryDataOrAxiosRes?.data?.memberships;
  if (!Array.isArray(memberships) || memberships.length === 0) return false;

  const latestMembership = memberships[memberships.length - 1];
  const expiryRaw = latestMembership?.MembershipExpiryDate;
  if (!expiryRaw) return false;

  const expiryDate = new Date(expiryRaw);
  if (Number.isNaN(expiryDate.getTime())) return false;

  return new Date() >= expiryDate;
}

/**
 * Protected Route Component
 * Redirects unauthenticated users to this app’s /login
 */
const ProtectedRoute = ({ children }) => {
  const { data: userData, isLoading, error, refetch } = useGetLoggedInUser();
  const queryClient = useQueryClient();
  const location = useLocation();
  const refetchAttempted = useRef(false);
  const expiryRedirectHandled = useRef(false);
  const isLoggedIn = hasValidUserData(userData);
  const membershipExpired = isMembershipExpired(userData);
  // Guests may view the marketplace landing and product/category pages without logging in.
  const isPublicGuestPath = !isLoggedIn && isPublicBrowsePath(location.pathname);
  // Juspay membership return: the user just paid and is landing on the finalizer
  // page, which will create the new plan. Their cached membership still looks
  // expired (the row doesn't exist yet), so the expiry guard below must NOT log
  // them out / bounce them to /renewal here — that would abort finalization and
  // drop them on the plans page (and, being logged out, break the next Buy Now).
  // Let the finalizer run; it refreshes user data and navigates on to the profile.
  // SidebarMenu's expiry guard shares this predicate — both must agree.
  const isMembershipFinalizing = isMembershipFinalizingPath(
    location.pathname,
    location.search,
  );

  const redirectToRenewal = async () => {
    try {
      await axios.get('/auth/logout', { withCredentials: true });
    } catch (e) {
      // Ignore logout network failures and continue with local cleanup.
    } finally {
      clearAuthToken(axios, v1Backend, apiClient);
      localStorage.clear();
      navigateToDashboard('/renewal');
    }
  };

  useEffect(() => {
    const redirectTimer = setTimeout(() => {
      if (isLoading || isLoggedIn || error) return;
      // Don't bounce guests off public browse pages.
      if (isPublicGuestPath) return;

      const justLoggedIn = localStorage.getItem('justLoggedIn');
      const hasToken = !!getStoredToken();
      const shouldTryRefetch = justLoggedIn || hasToken;

      if (shouldTryRefetch && !refetchAttempted.current) {
        refetchAttempted.current = true;
        refetch()
          .then((result) => {
            const updated = queryClient.getQueryData(['getLoggedInUser']);
            const valid = hasValidUserData(updated) || hasValidUserData(result?.data);
            if (!valid) navigateToDashboard('/login');
          })
          .catch(() => navigateToDashboard('/login'));
        return;
      }

      if (!shouldTryRefetch) {
        navigateToDashboard('/login');
      }
    }, 400);

    return () => clearTimeout(redirectTimer);
  }, [isLoggedIn, isLoading, error, refetch, queryClient, isPublicGuestPath]);

  useEffect(() => {
    if (isLoading || !isLoggedIn || !membershipExpired) return;
    // Don't bounce a user who is finalizing a just-paid membership.
    if (isMembershipFinalizing) return;
    if (expiryRedirectHandled.current) return;

    expiryRedirectHandled.current = true;
    redirectToRenewal();
  }, [isLoading, isLoggedIn, membershipExpired, isMembershipFinalizing]);

  // Show loader while checking authentication
  if (isLoading) {
    return <PageLoader />;
  }

  if (membershipExpired && !isMembershipFinalizing) {
    return <PageLoader />;
  }

  // Guests on public browse pages render straight through (no login required).
  if (isPublicGuestPath) {
    return <>{children}</>;
  }

  // If not logged in, show loader briefly before redirect (gives time for refetch)
  if (!isLoggedIn) {
    return <PageLoader />;
  }

  // User is authenticated, render children
  return <>{children}</>;
};

export default ProtectedRoute;
